/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Alexander Golshtein
 * @version 1.24.0
 * @since 1.14.0
 */

import { Fragment, useCallback } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { ELogBusinessEventName, ELogBusinessEventType, logBusinessEvent } from '@modules/logging';

import { Button, Skeleton } from '../../../../../components/common';
import { modalOpen } from '../../../../../modules/modal/modalActions';
import { useSubscriberIdContext } from '../../../context';
import { ESubscriberIdActions } from '../../../types';
import SubscriberIdError from '../../SubscriberIdError';
import { manageNumberTranslationSelector } from '../selectors';
import { useGetManageNumberMetaQuery, useGetManageNumberSimCardQuery } from '../store';
import ManageNumberSimCardModal from './ManageNumberSimCardModal';
import { ManageNumberSimCardSkeleton } from './ManageNumberSkeleton';
import { ManageNumberSimCardStyled } from './styled/ManageNumberStyled';


const ManageNumberSimCard = () => {

    const { subscriberId } = useSubscriberIdContext();

    const dispatch = useDispatch();

    /* Select State */

    const translationManageNumber = useSelector(manageNumberTranslationSelector());

    /* Api */

    const { isAllowSimCardSuggest, isAllowSimChange } = useGetManageNumberMetaQuery(subscriberId, {
        selectFromResult: ({ data }) => ({
            isAllowSimCardSuggest: data?.actions?.includes(ESubscriberIdActions.simCardSuggest) ?? false,
            isAllowSimChange: data?.actions?.includes(ESubscriberIdActions.simCardChange) ?? false,
        }),
    });

    const { data, error, isFetching, isSuccess, refetch } = useGetManageNumberSimCardQuery(subscriberId);

    /* Handlers */

    const onChangeHandler = () => {
        dispatch(logBusinessEvent({
            eventName: ELogBusinessEventName.USER_CHANGE_SIM_MASTER,
            type: ELogBusinessEventType.common,
        }));
        dispatch(modalOpen({
            render: props => <ManageNumberSimCardModal
                isAllowSimCardSuggest={isAllowSimCardSuggest}
                subscriberId={subscriberId}
                onCloseModal={props.onCloseModal}
            />,
        }));
    };

    const renderSimCardItem = useCallback((label: string, value?: string) => value && (
        <div className="sim-card__item">
            <div className="subscriber-id__title subscriber-id__title_light sim-card__label">{label}</div>
            {value}
        </div>
    ), []);

    if (error || (isSuccess && !data)) return <SubscriberIdError refetch={refetch}/>;
    if (isFetching) return <Skeleton isLoading render={ManageNumberSimCardSkeleton}/>;
    if (!data) return null;

    return (
        <ManageNumberSimCardStyled>
            <div className="subscriber-id__title subscriber-id__title_bold sim-card__title">
                {translationManageNumber.simCard.title}
            </div>
            {
                !!data.icc
                    ? <Fragment>
                        {renderSimCardItem(translationManageNumber.simCard.iccid, data.icc)}
                        {renderSimCardItem(translationManageNumber.simCard.puk1, data.puk1)}
                        {renderSimCardItem(translationManageNumber.simCard.puk2, data.puk2)}
                    </Fragment>
                    : <div>{translationManageNumber.simCard.empty}</div>
            }
            {
                isAllowSimChange && (
                    <Button
                        className="sim-card__change"
                        kind="mediumNormal"
                        onClick={onChangeHandler}
                    >
                        {
                            !data.icc
                                ? translationManageNumber.simCard.btn.add
                                : translationManageNumber.simCard.btn.change
                        }
                    </Button>
                )
            }
        </ManageNumberSimCardStyled>
    );
};

export default ManageNumberSimCard;
